import * as React from "react";
import Box from "@mui/material/Box";

import TextField from "@mui/material/TextField";
import Button from "@mui/material/Button";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import {
  useForm as ReactHookForm,
  SubmitHandler,
  Controller,
  useWatch,
} from "react-hook-form";
import Autocomplete from "@mui/material/Autocomplete";
import { Done, Close } from "@mui/icons-material";

import { FormControl, Stack, Typography } from "@mui/material";

import FormHelperText from "@mui/material/FormHelperText";
import { useForm, FormContextInterface } from "../context";
import { IParent } from "../data/formDefault";

const emailRegex =
  /^(?:([a-zA-Z0-9_\.\-])+\@(([a-zA-Z0-9\-])+\.)+([a-zA-Z0-9]{2,4}))?$/;
const phoneRegex =
  /^(\+\d{1,2}\s?)?1?\-?\.?\s?\(?\d{3}\)?[\s.-]?\d{4}[\s.-]?\d{4}$/;

const parentSchema = yup.object().shape({
  title: yup.string().required("Parent's title is required"),
  firstName: yup.string().required("Parent's first name is required"),
  surname: yup.string().required("Parent's surname is required"),
  email: yup
    .string()
    .required("Email is required")
    .matches(emailRegex, "Please enter a valid email address"),
  email2: yup
    .string()
    .trim()
    .matches(emailRegex, "Please enter a valid email address"),
  mobileNumber: yup
    .string()
    .required("Phone number is required")
    .matches(phoneRegex, "Please enter a valid phone number"),
  mobileNumber2: yup
    .string()
    .nullable() // Allows null or empty value
    .notRequired()
    .trim()
    .matches(phoneRegex, {
      message: "Please enter a valid phone number",
      excludeEmptyString: true,
    }),
});

const titleOptions = [
  {
    label: "Mr",
    value: "Mr",
  },
  {
    label: "Mrs",
    value: "Mrs",
  },
  {
    label: "Ms",
    value: "Ms",
  },
  {
    label: "Miss",
    value: "Miss",
  },
  {
    label: "Dr",
    value: "Dr",
  },
];

const Parent = ({ handleClick }: any) => {
  const {
    parentInfo,
    schoolInfo,
    studentInfo,
    setParentInfo,
    stepChange,
    handleSubmitLogActivity,
    activeStep,
    setIsValidParentForm,
    AddPreApplication,
  } = useForm() as FormContextInterface;
  const {
    register,
    handleSubmit,
    formState: { errors, isValid },
    control,
    setValue,
  } = ReactHookForm<IParent>({
    resolver: yupResolver(parentSchema),
    mode: "onSubmit",
    defaultValues: parentInfo,
  });

  React.useEffect(() => {
    setIsValidParentForm(isValid);
  }, [isValid, setIsValidParentForm]);

  const primaryPhone = useWatch({ control, name: "mobileNumber" });
  const secondaryPhone = useWatch({ control, name: "mobileNumber2" });
  const primaryEmail = useWatch({ control, name: "email" });
  const secondaryEmail = useWatch({ control, name: "email2" });
  const moveToAddressRef = React.useRef(false);
  React.useEffect(() => {
    if (primaryPhone && secondaryPhone && primaryPhone === secondaryPhone) {
      setValue("mobileNumber2", "");
    }

    if (primaryEmail && secondaryEmail && primaryEmail === secondaryEmail) {
      setValue("email2", "");
    }
  }, [primaryPhone, secondaryPhone, primaryEmail, secondaryEmail, setValue]);

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      e.preventDefault();
      moveToAddressRef.current = true;
      if (moveToAddressRef.current) {
        handleSubmit(onSubmit)();
        const inputElements =
          document.getElementsByClassName("custom-textfield");
        const firstInput = inputElements[0] as HTMLInputElement | undefined;
        if (firstInput) {
          handleClick();
        }
      }
    }
  };
  const onSubmit: SubmitHandler<IParent> = (data) => {
    setParentInfo(data);
    stepChange("addressDetails");
    handleSubmitLogActivity({
      ActivityType: "ContinueParentDetail",
    });
    var result = {
      ParentFirstname: data.firstName,
      ParentLastname: data.surname,
      email: data.email,
      email2: data.email2,
      mobile: data.mobileNumber,
      mobile2: data.mobileNumber2,
      firstName: studentInfo.studentFirstName,
      lastName: studentInfo.studentSurname,
      date: studentInfo.date,
      month: studentInfo.month,
      year: studentInfo.year,
      schoolId: schoolInfo.id,
    };
    AddPreApplication(result);
  };
  const parentclick = () => {
    handleSubmitLogActivity({
      ActivityType: "ParentDetailContinueButton",
    });
  };

  if (
    (schoolInfo?.signUpMethod == "School" &&
      schoolInfo?.schoolSignUp == true) ||
    schoolInfo?.signUpMethod == "MTech"
  ) {
    return (
      <Box marginY={5} id="parent-content">
        <Typography
          variant="h5"
          marginY={2}
          className="section_titles"
          id="parent-details"
          sx={{ fontSize: "1.1rem !important", fontWeight: "bold !important" }}
        >
          Parent's Details
        </Typography>
        <form
          onSubmit={handleSubmit(onSubmit)}
          style={{ paddingBottom: "40px" }}
        >
          <FormControl
            variant="outlined"
            fullWidth
            sx={{ "& > :not(style)": { my: 2 } }}
          >
            <Controller
              name="title"
              control={control}
              render={({ field }) => {
                const optionValue = titleOptions.find(
                  (option) => option.value === field.value
                ) || {
                  value: field.value,
                  label: field.value,
                };
                return (
                  <>
                    <FormControl fullWidth variant="standard">
                      <Autocomplete
                        selectOnFocus
                        clearOnBlur
                        handleHomeEndKeys
                        options={titleOptions}
                        fullWidth
                        freeSolo
                        value={optionValue}
                        onChange={(event: any, newValue: null | any) => {
                          setValue("title", newValue?.value || newValue || "");
                        }}
                        onInputChange={(event, newInputValue) => {
                          setValue("title", newInputValue);
                        }}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            variant="standard"
                            label={
                              <>
                                {"Title"}
                                <Box
                                  component={"span"}
                                  sx={{ pl: 0.5, fontSize: 20 }}
                                >
                                  *
                                </Box>
                              </>
                            }
                            placeholder="Type your title or select from the dropdown"
                            onInput={(e) => {
                              var InputElement = e.target as HTMLInputElement;
                              InputElement.value = InputElement.value
                                .toString()
                                .slice(0, 50);
                            }}
                          />
                        )}
                      />
                      <FormHelperText>
                        {errors.title && (
                          <Box component={"span"} sx={{ color: "error.main" }}>
                            {errors?.title?.message}
                          </Box>
                        )}
                      </FormHelperText>
                    </FormControl>
                  </>
                );
              }}
            />
            <Stack direction="row" spacing={2}>
              <TextField
                id="outlined-required"
                variant="standard"
                label={
                  <>
                    {"Parent's First Name"}
                    <Box component={"span"} sx={{ pl: 0.5, fontSize: 20 }}>
                      *
                    </Box>
                  </>
                }
                helperText={
                  errors.firstName && (
                    <Box component={"span"} sx={{ color: "error.main" }}>
                      {errors?.firstName?.message}
                    </Box>
                  )
                }
                {...register("firstName", { required: true })}
                fullWidth
                placeholder="Parent's First Name"
                inputProps={{ maxLength: 100 }}
              />

              <TextField
                id="outlined-required"
                variant="standard"
                label={
                  <>
                    {"Parent's Surname"}
                    <Box component={"span"} sx={{ pl: 0.5, fontSize: 20 }}>
                      *
                    </Box>
                  </>
                }
                helperText={
                  errors.surname && (
                    <Box component={"span"} sx={{ color: "error.main" }}>
                      {errors?.surname?.message}
                    </Box>
                  )
                }
                {...register("surname", { required: true })}
                fullWidth
                placeholder="Surname"
                inputProps={{ maxLength: 100 }}
              />
            </Stack>
            <Stack direction="row" spacing={2}>
              <TextField
                id="standard-helperText"
                variant="standard"
                label={
                  <>
                    {"Parent's Email"}
                    <Box component={"span"} sx={{ pl: 0.5, fontSize: 20 }}>
                      *
                    </Box>
                  </>
                }
                helperText={
                  errors.email && (
                    <Box component={"span"} sx={{ color: "error.main" }}>
                      {errors?.email?.message}
                    </Box>
                  )
                }
                {...register("email", { required: true })}
                fullWidth
                placeholder="Parent's Email"
              />

              <TextField
                id="standard-helperText"
                variant="standard"
                label="Secondary Email"
                helperText={
                  <span>
                    {errors.email2 && (
                      <Box component={"span"} sx={{ color: "error.main" }}>
                        {errors?.email2?.message}
                      </Box>
                    )}
                    <Box>
                      If correspondence is shared with e.g. a fee payer, spouse,
                      partner, nanny or guardian.
                    </Box>
                  </span>
                }
                {...register("email2")}
                fullWidth
                placeholder="Secondary Email"
                autoComplete="off"
              />
            </Stack>

            <Stack direction="row" spacing={2}>
              <TextField
                id="standard-helperText"
                variant="standard"
                label={
                  <>
                    {"Parent’s Phone"}
                    <Box component={"span"} sx={{ pl: 0.5, fontSize: 20 }}>
                      *
                    </Box>
                  </>
                }
                helperText={
                  errors.mobileNumber && (
                    <Box component={"span"} sx={{ color: "error.main" }}>
                      {errors?.mobileNumber?.message}
                    </Box>
                  )
                }
                {...register("mobileNumber", { required: true })}
                fullWidth
                placeholder="Parent’s Phone"
                onKeyDown={handleKeyDown}
              />

              <TextField
                id="standard-helperText"
                variant="standard"
                label={<>{"Secondary Phone"}</>}
                helperText={
                  errors.mobileNumber2 && (
                    <Box component={"span"} sx={{ color: "error.main" }}>
                      {errors?.mobileNumber2?.message}
                    </Box>
                  )
                }
                {...register("mobileNumber2", { required: false })}
                fullWidth
                placeholder="Secondary Phone"
                autoComplete="off"
                onKeyDown={handleKeyDown}
              />
            </Stack>
          </FormControl>

          <Box display={"flex"} justifyContent="center">
            <Button
              type="submit"
              variant="contained"
              style={{
                background: "#ff00d0",
                borderRadius: "10px",
                fontWeight: "bold",
              }}
              onClick={parentclick}
            >
              {activeStep.addressDetails ? (
                isValid ? (
                  <Done />
                ) : (
                  <Close />
                )
              ) : (
                "Continue"
              )}
            </Button>
          </Box>
        </form>
      </Box>
    );
  } else {
    return <Box id="parent-content"></Box>;
  }
};

export { Parent };
